import React, { useEffect, useRef, useState } from "react";
import { Amplify } from "aws-amplify";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { useForm } from "../shared/hooks/useForm";
import { validate } from "../shared/utils/validators";
import { useAuth } from "../core/auth/useAuth";

// Material UI Imports
import { CircularProgress } from "@mui/material";

// Auth Components
import SignUpView from "../features/auth/components/SignUpView";
import LoginView from "../features/auth/components/LoginView";
import LoginTwoFaView from "../features/auth/components/LoginTwoFaView";
import ForgotPasswordView from "../features/auth/components/ForgotPasswordView";
import ResetPasswordView from "../features/auth/components/ResetPasswordView";
import ConfirmEmailView from "../features/auth/components/ConfirmEmailView";
import AuthenticatedLayout from "../shared/components/layout/AuthenticatedLayout";

const AUTH_STATE_TO_PATH = {
  login: "/login",
  signup: "/sign-up",
  forgot: "/forgot-password",
  reset: "/reset-password",
  confirm: "/confirm-email",
  two_fa_login: "/two-fa",
};

const RESET_EMAIL_STORAGE_KEY = "pbx_reset_email";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
      userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
    },
  },
});

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    loginState,
    setLoginState,
    isActionLoading,
    isLoading,
    handleSignUp,
    handleSignUpConfirmation,
    handleLogin,
    handleLoginTwoFaVerification,
    handleSignOut,
    currentAuthenticatedUser,
    handleForgotPassword,
    handleResetPassword,
  } = useAuth();

  const textFieldStyle = {
    mb: 4,
    textColor: (theme) => theme.palette.primary.text,
  };

  const [formState, inputHandler] = useForm(
    {
      firstname: { value: "", isValid: false },
      lastname: { value: "", isValid: false },
      email: { value: "", isValid: false },
      client_id: { value: "", isValid: false },
      password: { value: "", isValid: false },
      confirm_password: { value: "", isValid: false },
    },
    false,
  );

  const [confirmationFormState, confirmationInputHandler] = useForm(
    {
      email: { value: "", isValid: false },
      confirmationCode: { value: "", isValid: false },
    },
    false,
  );

  const [loginFormState, loginInputHandler] = useForm(
    {
      email: { value: "", isValid: false },
      password: { value: "", isValid: false },
    },
    false,
  );

  const [forgotFormState, forgotInputHandler] = useForm(
    { email: { value: "", isValid: false } },
    false,
  );

  const [resetFormState, resetInputHandler] = useForm(
    {
      email: { value: "", isValid: false },
      password: { value: "", isValid: false },
      confirm_password: { value: "", isValid: false },
    },
    false,
  );
  const [isResetEmailLocked, setIsResetEmailLocked] = useState(false);
  const resetEmailPrefillRef = useRef("");

  const handleTextInputChange = (event, handler, validators) => {
    const { name, value } = event.target;
    handler(name, value, validate(value, validators));
  };

  const handleSignUpWrapper = (event) => handleSignUp(event, formState, inputHandler);
  const handleSignUpConfirmationWrapper = (event) =>
    handleSignUpConfirmation(event, confirmationFormState, confirmationInputHandler);
  const handleLoginWrapper = (event) => handleLogin(event, loginFormState, loginInputHandler);
  const handleForgotPasswordWrapper = (event) =>
    handleForgotPassword(event, forgotFormState, forgotInputHandler);
  const handleResetPasswordWrapper = (event) =>
    handleResetPassword(event, resetFormState, resetInputHandler);

  useEffect(() => {
    currentAuthenticatedUser();
  }, [currentAuthenticatedUser]);

 
  useEffect(() => {
    if (isLoading || loginState.state === "logged_in") return;
    if (
      location.pathname === "/reset-password" &&
      !(loginState.state === "login" && loginState.message)
    ) {
      if (loginState.state !== "reset") {
        setLoginState({ state: "reset", message: null });
      }
      return;
    }

    const targetPath = AUTH_STATE_TO_PATH[loginState.state];
    if (targetPath && location.pathname !== targetPath) {
      navigate(targetPath);
    }
  }, [isLoading, location.pathname, loginState.message, loginState.state, navigate, setLoginState]);

  useEffect(() => {
    if (isLoading) return;

    if (location.pathname !== "/reset-password") {
      resetEmailPrefillRef.current = "";
      setIsResetEmailLocked(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const emailFromQuery = params.get("email");
    const savedEmail = localStorage.getItem(RESET_EMAIL_STORAGE_KEY);
    const resolvedEmail = emailFromQuery || savedEmail || "";
    const prefillKey = `${location.search}|${resolvedEmail}`;

    if (emailFromQuery && emailFromQuery !== savedEmail) {
      localStorage.setItem(RESET_EMAIL_STORAGE_KEY, emailFromQuery);
    }

    setIsResetEmailLocked(Boolean(emailFromQuery));

    if (resolvedEmail && resetEmailPrefillRef.current !== prefillKey) {
      resetEmailPrefillRef.current = prefillKey;
      resetInputHandler("email", resolvedEmail, true);
    }
  }, [isLoading, location.pathname, location.search, resetInputHandler]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  // Authenticated — all routes go to AuthenticatedLayout; its internal wildcard
  // redirects any unknown path (including stale auth URLs) to /dashboard.
  if (loginState.state === "logged_in") {
    return (
      <Routes>
        <Route
          path="/*"
          element={
            <AuthenticatedLayout
              handleSignOut={handleSignOut}
              isActionLoading={isActionLoading}
            />
          }
        />
      </Routes>
    );
  }

  // Unauthenticated — route by URL path; unknown paths fall back to /login.
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <LoginView
            loginFormState={loginFormState}
            loginInputHandler={loginInputHandler}
            handleTextInputChange={handleTextInputChange}
            handleLogin={handleLoginWrapper}
            loginState={loginState}
            setLoginState={setLoginState}
            isActionLoading={isActionLoading}
            textFieldStyle={textFieldStyle}
          />
        }
      />
      <Route
        path="/sign-up"
        element={
          <SignUpView
            formState={formState}
            inputHandler={inputHandler}
            handleTextInputChange={handleTextInputChange}
            handleSignUp={handleSignUpWrapper}
            loginState={loginState}
            setLoginState={setLoginState}
            isActionLoading={isActionLoading}
          />
        }
      />
      <Route
        path="/two-fa"
        element={
          <LoginTwoFaView
            loginState={loginState}
            setLoginState={setLoginState}
            handleLoginTwoFaVerification={handleLoginTwoFaVerification}
            isActionLoading={isActionLoading}
            textFieldStyle={textFieldStyle}
          />
        }
      />
      <Route
        path="/forgot-password"
        element={
          <ForgotPasswordView
            forgotFormState={forgotFormState}
            forgotInputHandler={forgotInputHandler}
            handleTextInputChange={handleTextInputChange}
            handleForgotPassword={handleForgotPasswordWrapper}
            loginState={loginState}
            setLoginState={setLoginState}
            isActionLoading={isActionLoading}
            textFieldStyle={textFieldStyle}
          />
        }
      />
      <Route
        path="/reset-password"
        element={
          <ResetPasswordView
            resetFormState={resetFormState}
            resetInputHandler={resetInputHandler}
            handleTextInputChange={handleTextInputChange}
            handleResetPassword={handleResetPasswordWrapper}
            isEmailLocked={isResetEmailLocked}
            loginState={loginState}
            setLoginState={setLoginState}
            isActionLoading={isActionLoading}
            textFieldStyle={textFieldStyle}
          />
        }
      />
      <Route
        path="/confirm-email"
        element={
          <ConfirmEmailView
            confirmationFormState={confirmationFormState}
            confirmationInputHandler={confirmationInputHandler}
            handleTextInputChange={handleTextInputChange}
            handleSignUpConfirmation={handleSignUpConfirmationWrapper}
            loginState={loginState}
            setLoginState={setLoginState}
            isActionLoading={isActionLoading}
            textFieldStyle={textFieldStyle}
          />
        }
      />
      <Route path="/*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
